import { invariant } from '@seen/core/invariant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import type { ExtendedRelatedPerson } from '@seen/core/models/relatedPerson'
import type { JSX } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { AddressDisplay } from '#src/components/patient/detail-content/contact-tab/AddressDisplay.tsx'
import { GenderDisplay } from '#src/components/patient/detail-content/contact-tab/GenderDisplay.tsx'
import { LanguagesDisplay } from '#src/components/patient/detail-content/contact-tab/LanguagesDisplay.tsx'
import { NameDisplay } from '#src/components/patient/detail-content/contact-tab/NameDisplay.tsx'
import { PhoneDisplay } from '#src/components/patient/detail-content/contact-tab/PhoneDisplay.tsx'
import {
  FacesheetCard,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import { QuestionnaireItemForm } from '#src/components/questionnaire-form/questionnaire-item-form/QuestionnaireItemForm.tsx'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export interface RelatedPersonCardProps {
  relatedPerson: ExtendedRelatedPerson
}

export function RelatedPersonCard({
  relatedPerson,
}: RelatedPersonCardProps): JSX.Element {
  const notesItem = questionnaires.AdditionalInformationQuestionnaire.item.find(
    (i) => i.linkId === 'contact-note'
  )
  invariant(
    notesItem,
    'Contact note item not found in Additional Information Questionnaire'
  )

  const questionnaireResponseResult = useSearchOne(
    ExtendedQuestionnaireResponse,
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.AdditionalInformationQuestionnaire.url,
      subject: relatedPerson.reference,
    })
  )
  const questionnaireResponse = questionnaireResponseResult.data ?? null

  const profile = relatedPerson.toFhirResource()

  return (
    <FacesheetCard rumName="related_person">
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon asChild>
            <Avatar profile={profile} size="icon" />
          </FacesheetCardIcon>
          <FacesheetCardLabel>{relatedPerson.name}</FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
        </FacesheetCardTitle>
      </FacesheetCardHeader>
      <FacesheetCardContent>
        <QuestionnaireItemForm
          item={notesItem}
          label={notesItem.text}
          questionnaireResponse={questionnaireResponse}
        />
        <NameDisplay name={relatedPerson.name} profile={profile} />
        <GenderDisplay profile={relatedPerson} />
        <LanguagesDisplay profile={relatedPerson} />
        <PhoneDisplay
          phoneNumber={relatedPerson.primaryPhoneNumber}
          profile={profile}
        />
        <AddressDisplay
          address={relatedPerson.primaryAddress}
          profile={profile}
        />
      </FacesheetCardContent>
    </FacesheetCard>
  )
}
